import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IUser } from '../../lib/interfaces';
import { DS } from './designTokens';
import MutedBadge from './components/MutedBadge';
import PatientTile from './components/PatientTile';
import VideoTile, { thumbnailSx } from './components/VideoTile';
import StatusPill from './components/StatusPill';
import { RemoteCallParticipant, SelfParticipant } from './types';

// api-service sets the role attribute on every token. A role added there later
// still gets a label.
const roleLabelKey = (role?: string): string =>
  `videoVisit.role.${role ?? 'unknown'}`;

type Props = {
  self: SelfParticipant;
  patient: IUser;
  remote?: RemoteCallParticipant;
  others: RemoteCallParticipant[];
};

const CallInProgressParticipants = ({
  self,
  patient,
  remote,
  others,
}: Props) => {
  const { t } = useTranslation();
  const selfLabel = t('videoVisit.selfLabel');

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 1.5,
        p: '14px',
        borderRadius: '20px',
        overflow: 'hidden',
        bgcolor: DS.stage,
      }}
    >
      <PatientTile patient={patient} remote={remote} />
      <Box
        sx={{
          flex: 'none',
          alignSelf: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {others.map((other) => {
          const label = t(roleLabelKey(other.role), {
            defaultValue: t('videoVisit.role.unknown'),
          });
          return (
            <VideoTile
              key={other.participant.identity}
              track={other.camera}
              showVideo={!other.cameraOff}
              label={label}
              badge={other.micOff && <MutedBadge name={label} />}
              sx={thumbnailSx}
            />
          );
        })}
        <VideoTile
          track={self.camera}
          showVideo={!self.cameraOff}
          label={selfLabel}
          badge={self.micOff && <MutedBadge name={selfLabel} />}
          mirrored
          sx={thumbnailSx}
        />
      </Box>

      <StatusPill localQuality={self.quality} remote={remote} />
    </Box>
  );
};

export default CallInProgressParticipants;
